import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ActionStateEnum, HealthKPIEnum } from '@twaice-fe/shared/models';
import { ENERGY_ANALYTICS_FEATURE_KEY, safetyActionAdapter, State } from '../reducers/energy-analytics.reducer';

export const getEnergyAnalyticsState = createFeatureSelector<State>(ENERGY_ANALYTICS_FEATURE_KEY);

// region Safety Recommendation selectors
const safetyActionSelectors = safetyActionAdapter.getSelectors();

export const getRequiredActions = createSelector(getEnergyAnalyticsState, (state) =>
  safetyActionSelectors.selectAll(state.safetyRecommendationData.requiredActions)
);
export const getRequiredActionsEntities = createSelector(getEnergyAnalyticsState, (state) =>
  safetyActionSelectors.selectEntities(state.safetyRecommendationData.requiredActions)
);

export const getRecommendedActions = createSelector(getEnergyAnalyticsState, (state) =>
  safetyActionSelectors.selectAll(state.safetyRecommendationData.recommendedActions)
);

export const getAllActions = createSelector(getEnergyAnalyticsState, (state) => ({
  requiredActions: safetyActionSelectors.selectAll(state.safetyRecommendationData.requiredActions),
  recommendedActions: safetyActionSelectors.selectAll(state.safetyRecommendationData.recommendedActions),
}));

export const getRecommendedActionsEntities = createSelector(getEnergyAnalyticsState, (state) =>
  safetyActionSelectors.selectEntities(state.safetyRecommendationData.recommendedActions)
);

export const getSafetyRecommendationError = createSelector(
  getEnergyAnalyticsState,
  (state) => state.safetyRecommendationData.error
);
export const getSafetyRecommendationLoadingState = createSelector(
  getEnergyAnalyticsState,
  (state) => state.safetyRecommendationData.isLoading
);

export const getSafetyRecommendationOverviewState = createSelector(getEnergyAnalyticsState, (state) => ({
  [ActionStateEnum.REQUIRED]: state.safetyRecommendationData.requiredActions.ids.length,
  [ActionStateEnum.RECOMMENDED]: state.safetyRecommendationData.recommendedActions.ids.length,
  [ActionStateEnum.STABLE]: state.safetyRecommendationData.numberOfStableStrings,
}));

export const getComponentByComponentBk = ({ componentBk, actionType }: { componentBk: string; actionType: ActionStateEnum }) =>
  createSelector(
    actionType === ActionStateEnum.REQUIRED ? getRequiredActionsEntities : getRecommendedActionsEntities,
    (entities) => entities[componentBk] || undefined
  );

export const getSafetyRecommendationComponentDetails = createSelector(
  getEnergyAnalyticsState,
  (state) => state.safetyRecommendationComponentDetails
);

export const getSafetyRecommendationComponentDetailsEntities = createSelector(
  getSafetyRecommendationComponentDetails,
  (state) => state.kpiStates
);

export const getSafetyRecommendationComponentDetailsError = createSelector(
  getSafetyRecommendationComponentDetails,
  (state) => state.error
);

export const getSafetyRecommendationComponentDetailsLoadingState = createSelector(
  getSafetyRecommendationComponentDetails,
  (state) => state.isLoading
);

export const getSafetyMailNotificationConfig = createSelector(
  getEnergyAnalyticsState,
  (state) => state.safetyRecommendationData.notificationSettings
);
// endregion

// region System Ingestion Statistics selectors
export const getSystemIngestionStatisticsLastSeenLoadingState = createSelector(
  getEnergyAnalyticsState,
  (state) => state.systemIngestionStatistics.isLoading
);
export const getSystemIngestionStatisticsLastSeenTimestamp = createSelector(getEnergyAnalyticsState, (state) =>
  state.systemIngestionStatistics.lastSeen ? new Date(state.systemIngestionStatistics.lastSeen) : null
);
export const getSystemIngestionStatisticsLastSeenRefreshTimestamp = createSelector(
  getEnergyAnalyticsState,
  (state) => state.systemIngestionStatistics.lastRefresh ?? null
);
// endregion

// region Health KPIs region
export const getLatestHealthKPIState = (kpi: HealthKPIEnum) =>
  createSelector(getEnergyAnalyticsState, (state) => state.healthKPIData[kpi].latest);

export const getAggregatedHealthKPIState = (kpi: HealthKPIEnum) =>
  createSelector(getEnergyAnalyticsState, (state) => state.healthKPIData[kpi].aggregated);
// endregion

export const getSystemSiblingsEffectState = createSelector(
  getEnergyAnalyticsState,
  (state) => state.siblingEffect.hasSiblingEffect
);
